import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Lock, Search, Target, Users } from 'lucide-react'
import type { CheckedState } from '@radix-ui/react-checkbox'
import { Badge } from '@/components/ui/badge'
import { useDirty } from '@/hooks/useDirty'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { FormField } from '@/components/FormField'
import { RoleMemberSharePicker } from '@/components/RoleMemberSharePicker'
import { cn } from '@/lib/cn'
import { ApiError } from '@/lib/api'
import type { Agent, AgentPayload } from '@/features/agents/types'
import type { Visibility } from '@/features/workflows/types'
import { useCreateAgent, useUpdateAgent } from '@/features/agents/queries'
import { DATA_SCOPE_GROUPS, dataScopeLabel } from '@/features/agents/dataScope'
import { useAuth } from '@/auth/useAuth'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  agent: Agent | null
  onSaved?: () => void
}

export function AgentFormModal({ open, onOpenChange, agent, onSaved }: Props) {
  const { user } = useAuth()
  const currentUserId = user?.userId ? Number(user.userId) : null
  const createM = useCreateAgent()
  const updateM = useUpdateAgent()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [instructions, setInstructions] = useState('')
  const [visibility, setVisibility] = useState<Visibility>('private')
  const [tables, setTables] = useState<string[]>([])
  const [shareValue, setShareValue] = useState<{ sharedRoleIds: number[]; sharedUserIds: number[] }>({
    sharedRoleIds: [],
    sharedUserIds: [],
  })
  const [scopeSearch, setScopeSearch] = useState('')
  const [scopeExpanded, setScopeExpanded] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setScopeSearch('')
    setScopeExpanded(new Set())
    setName(agent?.name ?? '')
    setDescription(agent?.description ?? '')
    setInstructions(agent?.instructions ?? '')
    setVisibility(agent?.visibility ?? 'private')
    setTables(agent?.allowed_tables ?? [])
    setShareValue({
      sharedRoleIds: (agent?.shared_roles ?? []).map((r) => r.role_id),
      sharedUserIds: (agent?.shared_users ?? []).map((u) => u.user_id),
    })
  }, [open, agent])

  const busy = createM.isPending || updateM.isPending

  // Dirty-check the modal. Snapshot includes every editable field; sets
  // are compared as sorted arrays so deepEqual treats them as ordered
  // primitives. For NEW agents the baseline mirrors the empty state set
  // by the open-effect above, so any keystroke or checkbox tick unlocks
  // Save.
  const formSnapshot = useMemo(
    () => ({
      name: name.trim(),
      description: description.trim(),
      instructions: instructions.trim(),
      visibility,
      tables: [...tables].sort(),
      sharedRoleIds: [...shareValue.sharedRoleIds].sort((a, b) => a - b),
      sharedUserIds: [...shareValue.sharedUserIds].sort((a, b) => a - b),
    }),
    [name, description, instructions, visibility, tables, shareValue],
  )
  const baselineSnapshot = useMemo(() => {
    if (!agent) {
      return {
        name: '',
        description: '',
        instructions: '',
        visibility: 'private' as Visibility,
        tables: [] as string[],
        sharedRoleIds: [] as number[],
        sharedUserIds: [] as number[],
      }
    }
    return {
      name: agent.name ?? '',
      description: agent.description ?? '',
      instructions: agent.instructions ?? '',
      visibility: agent.visibility ?? 'private',
      tables: [...(agent.allowed_tables ?? [])].sort(),
      sharedRoleIds: (agent.shared_roles ?? [])
        .map((r) => r.role_id)
        .sort((a, b) => a - b),
      sharedUserIds: (agent.shared_users ?? [])
        .map((u) => u.user_id)
        .sort((a, b) => a - b),
    }
  }, [agent])
  const dirty = useDirty(formSnapshot, baselineSnapshot)

  // Data-scope filter + master controls. Mirrors the role editor's
  // permission UX so users moving between Team Management and the agent
  // builder don't have to learn a second pattern.
  const filteredScopeGroups = useMemo(() => {
    const q = scopeSearch.trim().toLowerCase()
    if (!q) return DATA_SCOPE_GROUPS
    return DATA_SCOPE_GROUPS
      .map((g) => ({
        ...g,
        tables: g.tables.filter(
          (t) =>
            dataScopeLabel(t).toLowerCase().includes(q) ||
            t.toLowerCase().includes(q) ||
            g.label.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.tables.length > 0)
  }, [scopeSearch])

  const visibleGroupLabels = useMemo(
    () => filteredScopeGroups.map((g) => g.label),
    [filteredScopeGroups],
  )
  const expandedVisibleCount = useMemo(
    () => visibleGroupLabels.reduce((n, l) => (scopeExpanded.has(l) ? n + 1 : n), 0),
    [scopeExpanded, visibleGroupLabels],
  )
  const allExpandedState: CheckedState =
    visibleGroupLabels.length === 0
      ? false
      : expandedVisibleCount === visibleGroupLabels.length
        ? true
        : expandedVisibleCount === 0
          ? false
          : 'indeterminate'

  const visibleTableCodes = useMemo(
    () => filteredScopeGroups.flatMap((g) => g.tables),
    [filteredScopeGroups],
  )
  const visibleSelectedCount = useMemo(
    () => visibleTableCodes.reduce((n, t) => (tables.includes(t) ? n + 1 : n), 0),
    [tables, visibleTableCodes],
  )
  const allSelectedState: CheckedState =
    visibleTableCodes.length === 0
      ? false
      : visibleSelectedCount === visibleTableCodes.length
        ? true
        : visibleSelectedCount === 0
          ? false
          : 'indeterminate'

  const totalScopeTables = useMemo(
    () => DATA_SCOPE_GROUPS.reduce((n, g) => n + g.tables.length, 0),
    [],
  )

  const toggleTable = (t: string) =>
    setTables((arr) => (arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]))

  const toggleScopeGroup = (label: string) =>
    setScopeExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      return next
    })

  const groupScopeState = (codes: string[]): 'all' | 'some' | 'none' => {
    if (codes.length === 0) return 'none'
    let on = 0
    for (const c of codes) if (tables.includes(c)) on++
    if (on === 0) return 'none'
    if (on === codes.length) return 'all'
    return 'some'
  }

  const setScopeGroup = (codes: string[], on: boolean) =>
    setTables((arr) => {
      const set = new Set(arr)
      for (const c of codes) {
        if (on) set.add(c)
        else set.delete(c)
      }
      return Array.from(set)
    })

  const toggleSelectAllScope = (next: CheckedState) => {
    if (next === true) {
      setTables((arr) => {
        const set = new Set(arr)
        for (const t of visibleTableCodes) set.add(t)
        return Array.from(set)
      })
    } else {
      setTables((arr) => {
        const set = new Set(arr)
        for (const t of visibleTableCodes) set.delete(t)
        return Array.from(set)
      })
    }
  }

  const toggleExpandAllScope = (next: CheckedState) => {
    if (next === true) {
      setScopeExpanded((prev) => {
        const out = new Set(prev)
        for (const l of visibleGroupLabels) out.add(l)
        return out
      })
    } else {
      setScopeExpanded((prev) => {
        const out = new Set(prev)
        for (const l of visibleGroupLabels) out.delete(l)
        return out
      })
    }
  }

  const submit = async (asDraft: boolean) => {
    setError(null)
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    if (!instructions.trim()) {
      setError('Instructions are required.')
      return
    }
    const effectiveVisibility: Visibility = asDraft ? 'private' : visibility
    const effectiveRoleIds =
      effectiveVisibility === 'shared' ? shareValue.sharedRoleIds : []
    const effectiveUserIds =
      effectiveVisibility === 'shared' ? shareValue.sharedUserIds : []
    if (
      effectiveVisibility === 'shared' &&
      effectiveRoleIds.length === 0 &&
      effectiveUserIds.length === 0
    ) {
      setError('Pick at least one role or member, or switch to "Only me" / "Whole team".')
      return
    }

    const payload: AgentPayload = {
      name: name.trim(),
      description: description.trim(),
      instructions: instructions.trim(),
      allowed_tables: tables,
      visibility: effectiveVisibility,
      shared_user_ids: effectiveUserIds,
      shared_role_ids: effectiveRoleIds,
    }

    try {
      if (agent) {
        await updateM.mutateAsync({ id: agent.id, body: payload })
      } else {
        await createM.mutateAsync(payload)
      }
      onSaved?.()
      onOpenChange(false)
    } catch (err) {
      // 402 = AI Gateway limit. The global LimitReachedDialog already
      // explains it - skip the inline duplicate.
      if (err instanceof ApiError && err.status === 402) return
      setError(messageFor(err))
    }
  }

  const title = agent ? 'Edit Agent' : 'New Agent'
  const subtitle = agent
    ? 'Update instructions, visibility, or the members who can use this agent.'
    : 'Create a reusable Smart Chat persona that shapes how the chat answers your questions.'

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={subtitle}
      className="max-w-2xl"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          {!agent ? (
            <Button
              variant="secondary"
              onClick={() => void submit(true)}
              disabled={!dirty || busy}
              title="Save as a private draft"
            >
              Save as Draft
            </Button>
          ) : null}
          <Button onClick={() => void submit(false)} disabled={!dirty || busy}>
            {agent ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 pt-2">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <FormField
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder="e.g. Accounts Receivable Analyst"
        />

        <FormField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          placeholder="One-line summary (optional)"
        />

        <FormField
          label="Instructions (system prompt)"
          required
          control={
            <Textarea
              value={instructions}
              onChange={(e) => setInstructions(e.currentTarget.value)}
              placeholder="e.g. Answer only AR questions in concise bullets."
              className="min-h-[120px]"
            />
          }
        />

        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Visibility
          </label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <VisOption
              icon={<Lock className="size-5" aria-hidden />}
              label="Only me"
              desc="Private to you"
              selected={visibility === 'private'}
              onClick={() => setVisibility('private')}
            />
            <VisOption
              icon={<Users className="size-5" aria-hidden />}
              label="Whole team"
              desc="Everyone in this workspace"
              selected={visibility === 'tenant'}
              onClick={() => setVisibility('tenant')}
            />
            <VisOption
              icon={<Target className="size-5" aria-hidden />}
              label="Specific roles"
              desc="Pick roles or members"
              selected={visibility === 'shared'}
              onClick={() => setVisibility('shared')}
            />
          </div>
        </div>

        {visibility === 'shared' ? (
          <RoleMemberSharePicker
            value={shareValue}
            onChange={setShareValue}
            currentUserId={currentUserId}
          />
        ) : null}

        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Allowed data scope (advisory)
              </label>
              <p className="mt-1 text-xs text-muted-foreground">
                Hint to the agent about which areas of your tenant data it should read.
              </p>
            </div>
            <span className="text-xs text-muted-foreground">
              {visibleSelectedCount} of {visibleTableCodes.length} selected
              {scopeSearch.trim() ? (
                <span className="ml-1 text-muted-foreground/70">
                  (filtered from {totalScopeTables})
                </span>
              ) : null}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[220px] flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                placeholder="Search data scope by name…"
                value={scopeSearch}
                onChange={(e) => setScopeSearch(e.currentTarget.value)}
                className="pl-9"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
              <Checkbox
                checked={allSelectedState}
                onCheckedChange={toggleSelectAllScope}
                disabled={visibleTableCodes.length === 0}
                aria-label="Select all data scopes"
              />
              Select all
            </label>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
              <Checkbox
                checked={allExpandedState}
                onCheckedChange={toggleExpandAllScope}
                disabled={visibleGroupLabels.length === 0}
                aria-label="Expand all data scope groups"
              />
              Expand all
            </label>
          </div>
          <div className="max-h-[40vh] space-y-1 overflow-y-auto rounded-md border p-2">
            {filteredScopeGroups.length === 0 ? (
              <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                No data scopes match &ldquo;{scopeSearch}&rdquo;.
              </p>
            ) : (
              filteredScopeGroups.map((g) => {
                const codes = g.tables
                const state = groupScopeState(codes)
                const isExpanded =
                  scopeExpanded.has(g.label) || scopeSearch.trim().length > 0
                const selectedInGroup = codes.reduce(
                  (n, c) => (tables.includes(c) ? n + 1 : n),
                  0,
                )
                return (
                  <div key={g.label} className="rounded-md border bg-muted/30">
                    <div className="flex items-center gap-2 px-2 py-1.5">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => toggleScopeGroup(g.label)}
                        className="h-auto flex-1 justify-start gap-1.5 p-1 text-left font-normal hover:bg-transparent"
                        aria-expanded={isExpanded}
                        aria-controls={`scope-group-${g.label}`}
                      >
                        {isExpanded ? (
                          <ChevronDown className="size-4 text-muted-foreground" aria-hidden />
                        ) : (
                          <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                        )}
                        <span className="text-sm font-medium">{g.label}</span>
                        <Badge variant="secondary" className="text-[10px]">
                          {selectedInGroup} / {codes.length}
                        </Badge>
                      </Button>
                      <label
                        className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={
                            state === 'all'
                              ? true
                              : state === 'some'
                                ? 'indeterminate'
                                : false
                          }
                          onCheckedChange={() => setScopeGroup(codes, state !== 'all')}
                          aria-label={`Select all ${g.label}`}
                        />
                        Select all
                      </label>
                    </div>
                    <div
                      id={`scope-group-${g.label}`}
                      className={cn(
                        'space-y-0.5 border-t bg-background px-2 py-1',
                        isExpanded ? '' : 'hidden',
                      )}
                    >
                      {g.tables.map((t) => (
                        <label
                          key={t}
                          className="flex items-start gap-2 rounded-sm p-1 hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={tables.includes(t)}
                            onCheckedChange={() => toggleTable(t)}
                            className="mt-0.5"
                          />
                          <span className="text-sm leading-tight">
                            <span>{dataScopeLabel(t)}</span>
                            <code className="ml-1 text-[10px] text-muted-foreground">{t}</code>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

function VisOption({
  icon,
  label,
  desc,
  selected,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  desc: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      className={cn(
        'flex h-auto flex-col items-center gap-1 rounded-md border-2 p-3 text-center transition',
        selected
          ? 'border-primary bg-primary/10 hover:bg-primary/15'
          : 'border-input hover:border-primary/40',
      )}
    >
      <span className="text-primary">{icon}</span>
      <span className="text-sm font-semibold">{label}</span>
      <span className="text-xs text-muted-foreground">{desc}</span>
    </Button>
  )
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return 'Failed to save agent.'
}
